import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CenterFocusStrong as FitIcon,
  OpenInNew as OpenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../DashboardContext';
import type { RecentTransaction, RecentTransactionType } from '../DashboardContext';
import {
  formatDashboardDate,
  formatRecentTransactionTimestamp,
} from '../dashboardHelpers';
import {
  TIMELINE_FETCH_LIMIT,
  buildTimelineDensity,
  countTimelineTypes,
  filterTimelineTransactions,
  groupTimelineByDay,
  clampTimelineWindow,
  isFullTimelineWindow,
  panTimelineWindow,
  sortTimelineTransactions,
  timelineInstant,
  timelineRange,
  timelineTxKey,
  zoomTimelineWindow,
  type TimelineWindow,
} from '../dashboardTimeline';
import { dashboardApi } from '../api';
import {
  RECENT_TX_TYPE_ORDER,
  styleForRecentTransaction,
} from '../recentTransactionMeta';
import { trackBackground, timelineWash } from '../dashboardChrome';
import {
  hasRecentReportReference,
  resolveRecentReportKind,
  toRecentReportBundle,
} from '../recentTransactionReport';
import RecentTransactionReports, {
  type RecentReportView,
} from './RecentTransactionReports';
import { useSessionState } from '../../../hooks/useSessionState';

const canHoverDevice = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

const formatClock = (tx: RecentTransaction): string => {
  const ms = timelineInstant(tx);
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (value: number, currency: string) =>
  `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)} ${currency}`;

const formatStripBound = (ms: number, withDate: boolean): string => {
  const date = new Date(ms);
  if (withDate) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const touchDistance = (
  a: { clientX: number; clientY: number },
  b: { clientX: number; clientY: number },
) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const TimelineDensityStrip = ({
  density,
  fullRange,
  viewWindow,
  zoomed,
  preview,
  onSelect,
  onWindowChange,
}: {
  density: ReturnType<typeof buildTimelineDensity>;
  fullRange: TimelineWindow;
  viewWindow: TimelineWindow;
  zoomed: boolean;
  preview: RecentTransaction | null;
  onSelect: (tx: RecentTransaction) => void;
  onWindowChange: (next: TimelineWindow | null) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const trackRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    x: number;
    lastX: number;
    raf: number;
    window: TimelineWindow;
  } | null>(null);
  const pinchRef = useRef<{ dist: number; anchor: number; window: TimelineWindow } | null>(null);
  const spanDays = viewWindow.end - viewWindow.start > 36 * 60 * 60 * 1000;

  const anchorFromClientX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0.5;
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  };

  const zoomBy = (factor: number, anchor01 = 0.5) => {
    onWindowChange(zoomTimelineWindow(viewWindow, fullRange, factor, anchor01));
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const outward = event.deltaY > 0;
      zoomBy(outward ? 1.18 : 1 / 1.18, anchorFromClientX(event.clientX));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [viewWindow, fullRange]);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography variant="caption" color="text.secondary">
          {t('common:dashboard.timeline.densityLabel')}
          {' · '}
          {formatStripBound(viewWindow.start, spanDays)}
          {' – '}
          {formatStripBound(viewWindow.end, spanDays)}
        </Typography>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <IconButton
            size="small"
            aria-label={t('common:dashboard.timeline.zoomOut')}
            onClick={() => zoomBy(1.35, 0.5)}
          >
            <ZoomOutIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('common:dashboard.timeline.zoomIn')}
            onClick={() => zoomBy(1 / 1.35, 0.5)}
          >
            <ZoomInIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            disabled={!zoomed}
            aria-label={t('common:dashboard.timeline.fit')}
            onClick={() => onWindowChange(null)}
          >
            <FitIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        ref={trackRef}
        role="slider"
        aria-label={t('common:dashboard.timeline.densityLabel')}
        tabIndex={0}
        onDoubleClick={() => onWindowChange(null)}
        onPointerDown={(event) => {
          if (event.pointerType === 'touch') return;
          (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
          dragRef.current = {
            x: event.clientX,
            lastX: event.clientX,
            raf: 0,
            window: viewWindow,
          };
        }}
        onPointerMove={(event) => {
          const drag = dragRef.current;
          const track = trackRef.current;
          if (!drag || !track) return;
          drag.lastX = event.clientX;
          if (drag.raf) return;
          drag.raf = window.requestAnimationFrame(() => {
            if (!dragRef.current || !trackRef.current) return;
            drag.raf = 0;
            const deltaPx = drag.lastX - drag.x;
            const deltaMs = -(deltaPx / trackRef.current.clientWidth) * (drag.window.end - drag.window.start);
            onWindowChange(panTimelineWindow(drag.window, fullRange, deltaMs));
          });
        }}
        onPointerUp={() => {
          if (dragRef.current?.raf) window.cancelAnimationFrame(dragRef.current.raf);
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          if (dragRef.current?.raf) window.cancelAnimationFrame(dragRef.current.raf);
          dragRef.current = null;
        }}
        onTouchStart={(event) => {
          if (event.touches.length !== 2) return;
          pinchRef.current = {
            dist: touchDistance(event.touches[0], event.touches[1]),
            anchor: anchorFromClientX((event.touches[0].clientX + event.touches[1].clientX) / 2),
            window: viewWindow,
          };
        }}
        onTouchMove={(event) => {
          const pinch = pinchRef.current;
          if (!pinch || event.touches.length !== 2) return;
          event.preventDefault();
          const dist = touchDistance(event.touches[0], event.touches[1]);
          if (pinch.dist <= 0) return;
          onWindowChange(
            zoomTimelineWindow(pinch.window, fullRange, pinch.dist / dist, pinch.anchor),
          );
        }}
        onTouchEnd={() => {
          pinchRef.current = null;
        }}
        onKeyDown={(event) => {
          if (event.key === '+' || event.key === '=') zoomBy(1 / 1.25, 0.5);
          if (event.key === '-' || event.key === '_') zoomBy(1.25, 0.5);
          if (event.key === '0' || event.key === 'Escape') onWindowChange(null);
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const span = viewWindow.end - viewWindow.start;
            onWindowChange(
              panTimelineWindow(
                viewWindow,
                fullRange,
                (event.key === 'ArrowLeft' ? -1 : 1) * span * 0.15,
              ),
            );
          }
        }}
        sx={{
          height: 40,
          position: 'relative',
          borderRadius: 999,
          bgcolor: trackBackground(theme),
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          touchAction: 'none',
          cursor: zoomed ? 'grab' : 'zoom-in',
          '&:active': { cursor: zoomed ? 'grabbing' : 'zoom-in' },
          outline: 'none',
          '&:focus-visible': {
            boxShadow: (mui) => `0 0 0 2px ${mui.palette.primary.main}`,
          },
        }}
      >
        {(() => {
          const span = Math.max(fullRange.end - fullRange.start, 1);
          const left = ((viewWindow.start - fullRange.start) / span) * 100;
          const width = ((viewWindow.end - viewWindow.start) / span) * 100;
          return (
            <Box
              sx={{
                position: 'absolute',
                left: `${left}%`,
                width: `${Math.max(width, 1.5)}%`,
                top: 0,
                bottom: 0,
                bgcolor: (mui) => timelineWash(mui, mui.palette.primary.main, 0.16),
                borderLeft: '1px solid',
                borderRight: '1px solid',
                borderColor: 'primary.main',
                pointerEvents: 'none',
              }}
            />
          );
        })()}
        {density.filter((tick) => tick.visible).map((tick) => {
          const color = styleForRecentTransaction(tick.tx.type, mode).color;
          const active =
            preview &&
            preview.id === tick.tx.id &&
            preview.type === tick.tx.type &&
            preview.createdAt === tick.tx.createdAt;
          return (
            <Box
              key={tick.key}
              component="button"
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onSelect(tick.tx);
              }}
              title={tick.tx.reference}
              sx={{
                position: 'absolute',
                left: `calc(${tick.position}% - 3px)`,
                top: active ? 6 : 10,
                width: active ? 8 : 6,
                height: active ? 24 : 16,
                p: 0,
                border: 0,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: color,
                opacity: active ? 1 : 0.92,
                boxShadow: active
                  ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${color}`
                  : `0 0 0 1px ${theme.palette.background.paper}`,
                transition: 'top 120ms, height 120ms, box-shadow 120ms',
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const ActivityTimeline = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const {
    dateRange,
    recentTransactions,
    currency,
    refreshGeneration,
    sectionLoading,
  } = useDashboard();
  const from = formatDashboardDate(dateRange.from);
  const to = formatDashboardDate(dateRange.to);

  const [rows, setRows] = useState<RecentTransaction[]>(() =>
    sortTimelineTransactions(recentTransactions),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useSessionState('activityTimeline:query', '');
  const [typeFilters, setTypeFilters] = useSessionState<RecentTransactionType[]>('activityTimeline:types', []);
  const [timeWindow, setTimeWindow] = useState<TimelineWindow | null>(null);
  const [preview, setPreview] = useState<RecentTransaction | null>(null);
  const [reportView, setReportView] = useState<RecentReportView | null>(null);
  const hoverCapable = useRef(canHoverDevice());
  const nodeRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const hoverTimerRef = useRef(0);

  useEffect(() => {
    if (recentTransactions.length && rows.length === 0) {
      setRows(sortTimelineTransactions(recentTransactions));
    }
  }, [recentTransactions, rows.length]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    dashboardApi
      .getSection('activity', from, to, { Limit: TIMELINE_FETCH_LIMIT, RecentOnly: 1 })
      .then((res) => {
        if (cancelled) return;
        const next = sortTimelineTransactions(res.data.recentTransactions || []);
        setRows(next);
        setPreview((current) => current ?? next[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, refreshGeneration]);

  const typeCounts = useMemo(() => countTimelineTypes(rows), [rows]);
  const filtered = useMemo(
    () => filterTimelineTransactions(rows, { types: typeFilters, query }),
    [rows, typeFilters, query],
  );
  const groups = useMemo(() => groupTimelineByDay(filtered), [filtered]);
  const fullRange = useMemo(() => timelineRange(filtered), [filtered]);
  const viewWindow = useMemo(() => {
    if (!fullRange) return null;
    return timeWindow ? clampTimelineWindow(timeWindow, fullRange) : fullRange;
  }, [fullRange, timeWindow]);
  const density = useMemo(
    () => buildTimelineDensity(filtered, viewWindow),
    [filtered, viewWindow],
  );
  const zoomed = !isFullTimelineWindow(viewWindow, fullRange);

  useEffect(() => {
    setTimeWindow(null);
  }, [from, to, query, typeFilters]);

  const toggleTypeFilter = (type: RecentTransactionType) => {
    setTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type],
    );
  };
  const truncated = !loading && rows.length >= TIMELINE_FETCH_LIMIT;

  const openReport = useCallback((tx: RecentTransaction) => {
    if (!hasRecentReportReference(tx)) return;
    setReportView({
      kind: resolveRecentReportKind(tx),
      bundle: toRecentReportBundle(tx),
    });
  }, []);

  const handleNodeEnter = (tx: RecentTransaction) => {
    window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      setPreview((current) =>
        current
        && current.id === tx.id
        && current.type === tx.type
        && (current.createdAt || '') === (tx.createdAt || '')
          ? current
          : tx,
      );
    }, 180);
  };

  const handleNodeLeave = () => {
    window.clearTimeout(hoverTimerRef.current);
  };

  const handleNodeClick = (tx: RecentTransaction) => {
    window.clearTimeout(hoverTimerRef.current);
    setPreview(tx);
    if (hoverCapable.current && hasRecentReportReference(tx)) {
      openReport(tx);
    }
  };

  useEffect(() => () => window.clearTimeout(hoverTimerRef.current), []);

  const nodeRefKey = (tx: RecentTransaction) =>
    `${tx.type}:${tx.id}:${tx.batch || ''}:${tx.createdAt || tx.date || ''}`;

  const scrollToTx = (tx: RecentTransaction) => {
    setPreview(tx);
    nodeRefs.current[nodeRefKey(tx)]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const firstMs = filtered.length ? timelineInstant(filtered[filtered.length - 1]) : 0;
  const lastMs = filtered.length ? timelineInstant(filtered[0]) : 0;

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
              {t('common:dashboard.timeline.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('common:dashboard.timeline.subtitle', {
                count: filtered.length,
                from: dateRange.from.toLocaleDateString(),
                to: dateRange.to.toLocaleDateString(),
              })}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label={`${filtered.length} ${t('common:dashboard.timeline.events')}`}
              sx={{ fontWeight: 600 }}
            />
            {lastMs > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${t('common:dashboard.timeline.last')}: ${formatClock(filtered[0])}`}
              />
            )}
            {firstMs > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`${t('common:dashboard.timeline.first')}: ${new Date(firstMs).toLocaleDateString()}`}
              />
            )}
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('common:dashboard.timeline.hoverHint')}
        </Typography>

        <TextField
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('common:dashboard.timeline.search')}
          sx={{ mt: 1.5, maxWidth: 420 }}
        />

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            label={`${t('common:dashboard.timeline.allTypes')} · ${rows.length}`}
            color={typeFilters.length === 0 ? 'primary' : 'default'}
            variant={typeFilters.length === 0 ? 'filled' : 'outlined'}
            onClick={() => setTypeFilters([])}
          />
          {RECENT_TX_TYPE_ORDER.filter((type) => typeCounts[type]).map((type) => {
            const style = styleForRecentTransaction(type, theme.palette.mode);
            const active = typeFilters.includes(type);
            return (
              <Chip
                key={type}
                size="small"
                icon={style.icon as ReactElement}
                label={`${t(`common:dashboard.recent.types.${type}`)} · ${typeCounts[type]}`}
                onClick={() => toggleTypeFilter(type)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  borderColor: style.color,
                  color: active ? theme.palette.getContrastText(style.color) : style.color,
                  bgcolor: active ? style.color : 'transparent',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            );
          })}
        </Stack>

        {density.length > 0 && fullRange && viewWindow && (
          <TimelineDensityStrip
            density={density}
            fullRange={fullRange}
            viewWindow={viewWindow}
            zoomed={zoomed}
            preview={preview}
            onSelect={scrollToTx}
            onWindowChange={setTimeWindow}
          />
        )}

        {truncated && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('common:dashboard.timeline.truncated', { shown: TIMELINE_FETCH_LIMIT })}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error">{t('common:dashboard.timeline.loadError')}</Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
          gridTemplateAreas: { xs: '"preview" "timeline"', lg: '"timeline preview"' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            gridArea: 'timeline',
            p: { xs: 1.25, md: 2 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            minHeight: 360,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {(loading || sectionLoading.activity) && rows.length === 0 ? (
            <Stack spacing={1.5} sx={{ py: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={72} />
              ))}
            </Stack>
          ) : filtered.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>{t('common:dashboard.timeline.empty')}</Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', pl: { xs: 3.5, sm: 4.5 } }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 14, sm: 18 },
                  top: 8,
                  bottom: 8,
                  width: 3,
                  borderRadius: 999,
                  background: (mui) =>
                    `linear-gradient(180deg, ${mui.palette.primary.main}, ${mui.palette.secondary.main})`,
                  opacity: 0.35,
                }}
              />
              <Stack spacing={2.5}>
                {groups.map((group) => (
                  <Box key={group.key}>
                    <Chip
                      size="small"
                      label={group.labelDate.toLocaleDateString(undefined, {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      sx={{
                        mb: 1.25,
                        fontWeight: 600,
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                    <Stack spacing={1.25}>
                      {group.items.map((tx, idx) => {
                        const style = styleForRecentTransaction(tx.type, theme.palette.mode);
                        const key = timelineTxKey(tx, idx);
                        const active =
                          preview &&
                          preview.id === tx.id &&
                          preview.type === tx.type &&
                          (preview.createdAt || '') === (tx.createdAt || '');
                        return (
                          <Box
                            key={key}
                            component="button"
                            type="button"
                            ref={(el: HTMLButtonElement | null) => {
                              nodeRefs.current[nodeRefKey(tx)] = el;
                            }}
                            onMouseEnter={() => handleNodeEnter(tx)}
                            onMouseLeave={handleNodeLeave}
                            onFocus={() => setPreview(tx)}
                            onClick={() => handleNodeClick(tx)}
                            aria-label={`${t(`common:dashboard.recent.types.${tx.type}`)} ${tx.reference}`}
                            sx={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: '1px solid',
                              borderColor: active ? style.color : 'divider',
                              bgcolor: (mui) =>
                                active
                                  ? timelineWash(mui, style.color, 0.16)
                                  : mui.palette.background.default,
                              borderRadius: 2.5,
                              p: 1.25,
                              pl: 1.5,
                              cursor: 'pointer',
                              position: 'relative',
                              color: 'text.primary',
                              transition: 'border-color 140ms, background-color 140ms, transform 140ms',
                              transform: active ? 'translateX(2px)' : 'none',
                              '&:hover': {
                                borderColor: style.color,
                                bgcolor: (mui) => timelineWash(mui, style.color, 0.12),
                              },
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: { xs: -27, sm: -31 },
                                top: 18,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: style.color,
                                boxShadow: active
                                  ? `0 0 0 5px ${timelineWash(theme, style.color, 0.35)}`
                                  : `0 0 0 3px ${theme.palette.background.paper}`,
                                border: '2px solid',
                                borderColor: 'background.paper',
                              }}
                            />
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                                <Chip
                                  size="small"
                                  icon={style.icon as ReactElement}
                                  label={t(`common:dashboard.recent.types.${tx.type}`)}
                                  sx={{
                                    height: 24,
                                    fontSize: '0.7rem',
                                    color: style.color,
                                    borderColor: style.color,
                                    bgcolor: 'transparent',
                                    '& .MuiChip-icon': { color: style.color },
                                  }}
                                  variant="outlined"
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {tx.reference || tx.batch || tx.id}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {formatAmount(tx.amount, currency)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {[tx.description, tx.depot, tx.user].filter(Boolean).join(' · ') || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatClock(tx)}
                              {tx.createdAt
                                ? ` · ${formatRecentTransactionTimestamp(tx.createdAt)}`
                                : ''}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {loading && rows.length > 0 && (
            <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            gridArea: 'preview',
            p: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 12,
            zIndex: 2,
            minHeight: 0,
            maxHeight: { xs: 'min(46vh, 420px)', lg: 'calc(100dvh - 11rem)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: (mui) =>
              preview
                ? `linear-gradient(165deg, ${timelineWash(mui, styleForRecentTransaction(preview.type, mui.palette.mode).color, 0.22)}, ${mui.palette.background.paper} 46%)`
                : mui.palette.background.paper,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1.2, fontWeight: 700, flexShrink: 0 }}
          >
            {t('common:dashboard.timeline.inspector')}
          </Typography>
          {preview ? (
            <TimelineInspector
              tx={preview}
              currency={currency}
              onOpen={() => openReport(preview)}
            />
          ) : (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {t('common:dashboard.timeline.noPreview')}
            </Typography>
          )}
          {!isDesktop && preview && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, flexShrink: 0 }}>
              {t('common:dashboard.timeline.touchHint')}
            </Typography>
          )}
        </Paper>
      </Box>

      <RecentTransactionReports
        reportView={reportView}
        onClose={() => setReportView(null)}
      />
    </Stack>
  );
};

const TimelineInspector = memo(({
  tx,
  currency,
  onOpen,
}: {
  tx: RecentTransaction;
  currency: string;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const style = styleForRecentTransaction(tx.type, theme.palette.mode);
  const canOpen = hasRecentReportReference(tx);
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [tx.type, tx.id, tx.createdAt, tx.batch]);

  const rows: Array<[string, string]> = [
    [t('common:dashboard.recent.type'), t(`common:dashboard.recent.types.${tx.type}`)],
    [t('common:dashboard.recent.reference'), tx.reference || tx.batch || tx.id],
    [t('common:dashboard.recent.amount'), formatAmount(tx.amount, currency)],
    [t('common:dashboard.recent.depot'), tx.depot || '—'],
    [t('common:dashboard.recent.user'), tx.user || '—'],
    [t('common:dashboard.recent.description'), tx.description || '—'],
    [t('common:dashboard.timeline.created'), formatRecentTransactionTimestamp(tx.createdAt)],
    [t('common:dashboard.timeline.updated'), formatRecentTransactionTimestamp(tx.updatedAt)],
  ];

  return (
    <Box sx={{ mt: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={scrollRef}
        onWheel={(event) => {
          const el = scrollRef.current;
          if (el && el.scrollHeight > el.clientHeight) event.stopPropagation();
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          pr: 0.75,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: (mui) => timelineWash(mui, style.color, 0.2),
              color: style.color,
            }}
          >
            {style.icon}
          </Box>
          <Box minWidth={0}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {t(`common:dashboard.recent.types.${tx.type}`)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
            >
              {tx.reference || tx.batch || tx.id}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
          {formatAmount(tx.amount, currency)}
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={0.75}>
          {rows.map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
      <Button
        fullWidth
        variant="contained"
        startIcon={<OpenIcon />}
        disabled={!canOpen}
        onClick={onOpen}
        sx={{ mt: 1.5, flexShrink: 0, textTransform: 'none', fontWeight: 700 }}
      >
        {t('common:dashboard.timeline.openReport')}
      </Button>
    </Box>
  );
});

export default ActivityTimeline;
